import { forwardRef } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { useWorkspacesFeatureEnabled } from '@editor/global/growthbookUtils';
import { Button, primaryTheme } from '@repo/ui/components/Buttons';
import CrownIcon from '@repo/ui/components/Icon/CrownIcon';
import PricingCheckIcon from '@repo/ui/components/Icon/PricingCheckIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, H6Plus, P2, P3 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import {
  MonetaryValue,
  PricingPlanAddon,
  PricingPlanPrice,
} from '../../types/Pricing';
import { formatMonetaryValue, getYearlyCharge } from '../../utils/billing';

import InfoIconModalPopover from './InfoIconModalPopover';
import {
  PriceLabel,
  Feature,
  PlanContainer,
  PlanContent,
  LabelWrapper,
  OfferBadge,
  Strikethrough,
  ContentPlanBadge,
  Features,
  PriceCustomLabel,
} from './styles';
import { PricingPlanProps } from './types';
import { getPlanCostsPerMonth } from './utils';

const getYearlyLabel = (
  planPrice: PricingPlanPrice['yearly'],
  addon?: PricingPlanAddon,
  quantity = 1
): string => {
  const { amount, currency } = planPrice;
  const price: MonetaryValue = {
    currency,
    amount: amount * quantity + (addon?.amount ?? 0),
  };
  const formattedPrice = formatMonetaryValue(getYearlyCharge(price), {
    fractionDigits: 0,
  });

  return `Billed ${formattedPrice} annually`;
};

/**
 * display data for a pricing plan
 */
const PricingPlan = forwardRef<HTMLDivElement, PricingPlanProps>(
  (props, ref) => {
    const { yearly: yearlyPricePerMonth, monthly: monthlyPricePerMonth } =
      props.price
        ? getPlanCostsPerMonth({
            planPricing: props.price,
            yearly: props.yearly,
            quantity: props.quantity,
            selectedAddon: props.selectedAddon,
          })
        : { yearly: null, monthly: null };

    const priceLabel =
      props.price?.label ??
      (props.yearly && props.price
        ? getYearlyLabel(
            props.price.yearly,
            props.selectedAddon,
            props.quantity
          )
        : '');

    const isEnabled = useWorkspacesFeatureEnabled();

    return (
      <PlanContainer
        ref={ref}
        className={props.className}
        hasLabel={!!props.label}
        state={props.state}
      >
        {props.label && (
          <LabelWrapper>
            <H6Plus color={themeStyle.inkBase}>{props.label}</H6Plus>
          </LabelWrapper>
        )}
        <PlanContent center={props.centerContent} hasLabel={!!props.label}>
          {props.isContentPlan && <ContentPlanBadge />}
          <FlexColumn
            alignItems="center"
            style={{ width: '100%', textAlign: 'center' }}
          >
            <FlexRow>
              <H2>{props.title}</H2>
            </FlexRow>
            <Spacer height="12px" />
            {typeof props.description === 'string' ? (
              <P2
                color={themeStyle.varInkMedium}
                dangerouslySetInnerHTML={{ __html: props.description }}
                style={{ maxWidth: '220px' }}
              />
            ) : (
              <P2 color={themeStyle.varInkMedium} style={{ maxWidth: '220px' }}>
                {props.description}
              </P2>
            )}
            <Spacer height="24px" />
            {monthlyPricePerMonth && yearlyPricePerMonth && (
              <>
                {props.type ? (
                  <PriceLabel>
                    <em>
                      {formatMonetaryValue(
                        props.yearly
                          ? yearlyPricePerMonth
                          : monthlyPricePerMonth,
                        {
                          fractionDigits: 0,
                        }
                      )}
                    </em>
                    {yearlyPricePerMonth.amount !== 0 && (
                      <FlexColumn>
                        {props.yearly && (
                          <OfferBadge>
                            {formatMonetaryValue(monthlyPricePerMonth, {
                              fractionDigits: 0,
                            })}
                            <Strikethrough />
                          </OfferBadge>
                        )}
                        <>/ month {isEnabled ? 'per editor' : ''}</>
                      </FlexColumn>
                    )}
                  </PriceLabel>
                ) : (
                  <PriceCustomLabel>Custom</PriceCustomLabel>
                )}
                <Spacer height="10px" />
                <P3
                  color={themeStyle.varInkMedium}
                  style={{ minHeight: '19px' }}
                >
                  {priceLabel}
                </P3>
              </>
            )}
            {props.memberSelect && (
              <>
                <Spacer height="14px" />
                {props.memberSelect}
              </>
            )}
            {props.button && (
              <>
                <Spacer height="18px" />
                <Button
                  name="pricingPlanActionButton"
                  {...props.button}
                  disabled={props.button?.disabled}
                  height="40px"
                  label={props.button?.label ?? props.buttonLabel}
                  theme={props.button?.theme ?? primaryTheme}
                  width="100%"
                  onClick={props.button.onClick}
                />
                <Spacer height="24px" />
              </>
            )}
          </FlexColumn>
          <FlexColumn style={{ width: '100%' }}>
            <Features>
              <ul>
                {props.mainFeatures.map((feature, index) => {
                  return (
                    <Feature key={index}>
                      <PricingCheckIcon
                        height="18px"
                        theme={{
                          color: themeStyle.varBlueMedium,
                        }}
                      />

                      <div className="content">
                        <span className="label">
                          {feature.bold ? (
                            <b>{feature.label}</b>
                          ) : (
                            feature.label
                          )}
                          {feature.premium && (
                            <i className="icon">
                              <CrownIcon height="10px" />
                            </i>
                          )}
                          {feature.infoIconModalContent && (
                            <InfoIconModalPopover
                              infoModalData={feature.infoIconModalContent}
                            />
                          )}
                        </span>
                        <span className="details">{feature.details}</span>
                      </div>
                    </Feature>
                  );
                })}
                {props.otherFeatures &&
                  props.otherFeatures.map((feature, index) => {
                    return (
                      <Feature key={index}>
                        <PricingCheckIcon
                          height="18px"
                          theme={{
                            color: themeStyle.varBlueMedium,
                          }}
                        />
                        <div className="content">
                          <span className="label">
                            {feature.bold ? (
                              <b>{feature.label}</b>
                            ) : (
                              feature.label
                            )}
                            {feature.premium && (
                              <i className="icon">
                                <CrownIcon height="10px" />
                              </i>
                            )}
                            {feature.infoIconModalContent && (
                              <InfoIconModalPopover
                                infoModalData={feature.infoIconModalContent}
                              />
                            )}
                          </span>
                          <span className="details">{feature.details}</span>
                        </div>
                      </Feature>
                    );
                  })}
              </ul>
            </Features>
          </FlexColumn>
        </PlanContent>
      </PlanContainer>
    );
  }
);

PricingPlan.displayName = 'PricingPlan';

export default PricingPlan;
